import { mergeWith } from 'lodash';

const customizer = (_objValue: unknown, srcValue: unknown) => {
  // Arrays are replaced wholesale — last non-undefined wins.
  if (Array.isArray(srcValue)) {
    return srcValue;
  }
  return undefined;
};

/**
 * Deep merge of preference layers with `code ← board ← user` precedence.
 * - Objects are deep-merged recursively.
 * - Arrays are replaced (no element-wise concat/merge).
 * - `undefined` in a higher layer means "no opinion" — falls through to lower layer.
 *
 * The user layer may be a `Partial<T>` (user has explicit overrides for
 * only some fields); the board layer is always a full `T` snapshot.
 */
export const deepMergePreferences = <T extends Record<string, unknown>>(
  codeDefault: T,
  board: T | null | undefined,
  user: Partial<T> | null | undefined,
): T => {
  const layers = [codeDefault, board ?? undefined, user ?? undefined].filter(
    (layer): layer is T | Partial<T> => layer !== undefined,
  );

  if (layers.length === 1) {
    return layers[0] as T;
  }

  return mergeWith({}, ...layers, customizer) as T;
};

/**
 * Merge a partial user-layer patch into the existing user-layer base, using
 * the same semantics as {@link deepMergePreferences} (objects merge, arrays
 * replace). Used by `useUserPreference#setValue` to produce the next pure
 * user-layer snapshot to persist as `user.data`.
 *
 * Unlike `deepMergePreferences`, the result remains a `Partial<T>` because
 * the user layer is allowed to omit fields — those fields fall through to
 * the board / code defaults at read time.
 */
export const mergeUserLayerPatch = <T extends Record<string, unknown>>(
  base: Partial<T>,
  patch: Partial<T>,
): Partial<T> => mergeWith({}, base, patch, customizer) as Partial<T>;
